import React, { useEffect, useMemo, useState, useRef } from "react";
import { connect } from 'react-redux';
import { useHistory } from "react-router-dom";
import ReactTable from 'react-table';
import { Button, Row, Col, Tooltip, Typography, Radio, Input } from 'antd';
import { SearchOutlined, FolderOpenFilled, ArrowRightOutlined, CheckOutlined, DownloadOutlined } from '@ant-design/icons';
import Papa from 'papaparse'
import moment from "moment";

import { setPageTitle } from "../../../Dashboard/DashboardActions";
import { getAllCasesRequest } from "../../CaseApiActions";
import UtilHelper from "../../../../utils/UtilHelper";
import SymptomsQueryTags from "../GenaScreen/SymptomsQueryTags/SymptomsQueryTags";

import './SearchHistory.css'
import HttpRequest from "../../../../utils/HttpRequest";
import SaveCase from "../SaveCaseV2/SaveCase";
import DataGrid from '../../../../utils/components/DataGrid/DataGrid'
import CustomIcon from "../../../../utils/components/CustomIcon/CustomIcon";
import Notification from "../../../../utils/Notification";

const FILTER_OPTIONS = {
  ALL: 'ALL',
  SAVED: 'SAVED'
}
const notification = new Notification()

const SearchHistoryComponent = (props) => {
  const dataGridRef = useRef(null)
  const { ownOnly = true } = props
  const history = useHistory()
  const [searchHistory, setSearchHistory] = useState([])
  const [tableData, setTableData] = useState([])
  const [filter, setFilter] = useState(FILTER_OPTIONS.ALL)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [reloadCounter, setReloadCounter] = useState(0);

  const [showSaveCaseModal, setShowSaveCaseModal] = useState(false)
  const [selectedSearchRecord, setSelectedSearchRecord] = useState(null)

  async function fetchData(params) {
    const res = await new HttpRequest(null, `/searchHistory`, 'get', null, null, { queryParams: params }).send()
    return res.data.data
  }

  useEffect(() => {
    setLoading(true)
    const params = { limit: 100000, ownOnly }
    if (filter === FILTER_OPTIONS.SAVED) {
      params.savedOnly = true
    }
    fetchData(params).then((data) => {
      setTableData(data)
    }).catch((err) => {
      setError(err)
    }).finally(() => {
      setLoading(false)
    })
  }, [ownOnly, filter, reloadCounter])

  const handleSaveCase = (data) => {
    setShowSaveCaseModal(true)
    setSelectedSearchRecord(data)
  }

  // callback on save case
  const onSaveCase = (obj) => {
    setShowSaveCaseModal(false)
    setReloadCounter(prev => prev + 1)
  }

  const columnDefs = useMemo(() => {
    return [
      {
        headerName: 'Created',
        field: 'createdAt',
        maxWidth: 150,
        getQuickFilterText: (params) => moment(params.data.createdAt).format('DD/MM/YYYY'),
        cellRendererFramework: (params) => moment(params.data.createdAt).format('DD/MM/YYYY'),
      },
      ...(!ownOnly ? [{
        headerName: 'User',
        maxWidth: 200,
        valueGetter: (params) => {
          const first = params.data.User.firstName || '';
          const last = params.data.User.lastName || '';
          return `${first} ${last}`.trim();
        }
      }] : []),
      {
        headerName: 'Symptoms',
        field: 'query',
        autoHeight: true,
        cellRendererFramework: (params) =>
          <SymptomsQueryTags node={params.data.queryJSON} />
      },
      {
        headerName: 'Clinical Features',
        field: 'clinicalFeatures',
        maxWidth: 250,
        autoHeight: true
      },
      {
        headerName: 'Test Requested',
        maxWidth: 200,
        cellClass: 'cell-center-alligned',
        valueGetter: (params) => params.data?.CaseVersion?.SuggestedTests?.length > 0, 
        cellRendererFramework: (params) => (
          <>
            {params.data?.CaseVersion?.SuggestedTests?.length > 0 && (
              <CustomIcon name="Check" width={24} height={24} />
            )}
          </>
        ),
      },
      {
        headerName: 'Save',
        maxWidth: 100,
        cellClass: 'cell-center-alligned',
        valueGetter: (params) => params.data?.CaseVersion !== null,
        cellRendererFramework: (params) => (
          <>
            {params.data?.CaseVersion !== null ? (
              <Tooltip title="Saved">
                <CustomIcon name="OpenFolder" width={24} height={24} />
              </Tooltip>
            ) : (
              <Tooltip title="Save as Case">
                <CustomIcon
                  name="LineArrowRight"
                  width={24}
                  height={24}
                  onClick={() => handleSaveCase(params.data)}
                />
              </Tooltip>
            )}
          </>
        ),
      },
      {
        headerName: '',
        maxWidth: 75,
        sortable: false,
        cellClass: 'cell-center-alligned',
        cellRendererFramework: (params) => (
          <>
            <Tooltip title="Resume search">
              <CustomIcon
                name="Search"
                width={24}
                height={24}
                onClick={() => {
                  history.push(`/search?searchRecordId=${params.data.id}`);
                }}
              />
            </Tooltip>
          </>
        ),
      },
    ];
  }, [ownOnly])

  const exportCSV = () => {
    dataGridRef.current.api.exportDataAsCsv({
      fileName: `search-history-${moment().format('DD-MM-YYYY')}.csv`,
    });
    notification.show('info', 'Data Exported');
    // const filename = `search-history-${moment().format("DD-MM-YYYY")}.csv`
    // const csv = Papa.unparse(tableData); // Convert JSON to CSV
    // const blob = new Blob([csv], { type: "text/csv" });
    // const url = URL.createObjectURL(blob);

    // const a = document.createElement("a");
    // a.href = url;
    // a.download = filename;
    // document.body.appendChild(a);
    // a.click();
    // document.body.removeChild(a);
  };

  useEffect(() => {
    if (!dataGridRef?.current?.api) return
    const searchText = debouncedSearch.trim()
    dataGridRef.current.api.setQuickFilter(searchText);
  }, [dataGridRef, debouncedSearch])

  // Debounce effect on search
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 600);

    // Cancel timeout if search text changes before 600ms
    return () => clearTimeout(handler);
  }, [search]);

  return (
    <div className="flex-vertical" style={{ gap: '16px' }}>
      <div className="flex-horizontal" style={{ justifyContent: "space-between" }}>
        <Typography.Title level={2} style={{ margin: 0 }}>Search History</Typography.Title>
        <Button onClick={exportCSV}>Export<CustomIcon name="Download" /></Button>
      </div>
      <div className="flex-horizontal-gap-2">
        <Radio.Group style={{ minWidth: 'max-content' }} value={filter} onChange={(e) => setFilter(e.target.value)}>
          <Radio.Button value={FILTER_OPTIONS.ALL}>All</Radio.Button>
          <Radio.Button value={FILTER_OPTIONS.SAVED}>Saved</Radio.Button>
        </Radio.Group>
        <Input
          allowClear
          placeholder="Search"
          prefix={<CustomIcon name="Search" />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      {/* <ReactTable
        filterable={false}
        defaultFilterMethod={UtilHelper.filterColumnReactTable}
        defaultPageSize={10}
        noDataText='No records present. Click Add case to create a new case'
        data={tableData}
        columns={columns}
        loading={loading}
        className="-striped -highlight"
      /> */}
      <DataGrid
        isLoading={loading}
        gridRef={dataGridRef}
        columnDefs={columnDefs}
        rowData={tableData}
        gridOptions={{
          quickFilterText: debouncedSearch
        }}
      />
      <SaveCase open={showSaveCaseModal} onClose={() => setShowSaveCaseModal(false)} onSave={onSaveCase} searchRecord={selectedSearchRecord} />
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    cases: state.caseReducer.cases,
    userResources: state.authorisation.userResources,
    loggedInUser: state.auth.loggedInUser,
    currentRole: state.auth.currentRole
  }
};

const mapDispatchToProps = (dispatch) => {
  return {
    getAllCases: (id) => {
      return dispatch(getAllCasesRequest(id)).then((res) => {
        return Promise.resolve(res);
      }, (error) => {
        return Promise.reject(error);
      });
    },
    updatePageTitle: (pageTitle, enablePageTitle) => {
      dispatch(setPageTitle(pageTitle, enablePageTitle));
    },
  }
};

const SearchHistory = connect(
  mapStateToProps,
  mapDispatchToProps
)(SearchHistoryComponent);

export default SearchHistory;
